// Admin screens: overview and library sync, batches, students in a batch, people, settings.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, navigate, useRoute } from '../router.js';
import { forgetGraphToken, graphToken, requestGraphAccess, rpc } from '../api.js';
import { resolveFolder, syncLibrary } from '../graph.js';
import { applyGroupChanges } from '../groups.js';
import { SUBJECTS } from '../library-parse.js';
import { Icon } from '../ui/icons.js';
import { Shell } from '../ui/shell.jsx';
import { Button, Chip, Empty, ErrorBox, Field, Loading, Notice, Section, Sheet, useRpc, useToast } from '../ui/components.jsx';
import { addMonths, dateShort, dateTime, initialOf, monthYear, msUrl, plural, todayIso } from '../format.js';
import { ReceiptDetails } from './fees.jsx';
import { RELATION_LABEL } from './parents.jsx';

const SOURCES = ['Silver plan', 'Gold plan', 'Platinum plan', 'Per paper', 'Revision kit', 'Other'];

/* ---------------------------------------------------------------- Overview + sync */
export function AdminHome() {
  const { query } = useRoute();
  const toast = useToast();
  const { data, error, loading, reload } = useRpc('admin_overview');
  const [progress, setProgress] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [result, setResult] = useState(null);
  const started = useRef(false);
  const root = data && data.library && data.library.root;
  const last = data && data.library && data.library.last_sync;

  const runSync = async () => {
    setSyncError(null);
    setResult(null);
    const token = graphToken();
    if (!token) { await requestGraphAccess('/admin?sync=1'); return; }
    try {
      setProgress({ phase: 'listing' });
      const res = await syncLibrary(token, root, rpc, setProgress);
      setResult(res.stats);
      toast(`Library updated: ${plural(res.stats.batches, 'batch', 'batches')}, ${plural(res.stats.classes, 'class', 'classes')}`);
      reload(true);
    } catch (e) {
      if (e.reason === 'graph_auth') forgetGraphToken();
      setSyncError(e);
    } finally {
      setProgress(null);
    }
  };

  useEffect(() => {
    if (!data || started.current || query.get('sync') !== '1') return;
    started.current = true;
    navigate('/admin', { replace: true });
    if (root && graphToken()) runSync();
  }, [data]);

  return (
    <Shell title="Admin">
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <div class="stats">
            <div class="stat"><strong>{data.counts.batches}</strong><span>Batches</span></div>
            <div class="stat"><strong>{data.counts.classes}</strong><span>Classes</span></div>
            <div class="stat"><strong>{data.counts.active_enrolments}</strong><span>Active enrolments</span></div>
            <div class="stat"><strong>{data.counts.never_signed_in}</strong><span>Not signed in yet</span></div>
          </div>

          <Section title="Lecture library" id="a-library">
            <div class="card library">
              {root ? (
                <>
                  <p class="library-root"><Icon name="folder" /><span><strong>{root.name}</strong> folder in OneDrive{msUrl(root.web_url) ? <> · <a href={msUrl(root.web_url)} target="_blank" rel="noopener">Open</a></> : null}</span></p>
                  <p class="muted">{last ? `Last updated ${dateTime(last.at)} by ${last.by}: ${plural((last.stats && last.stats.batches) || 0, 'batch', 'batches')}, ${plural((last.stats && last.stats.classes) || 0, 'class', 'classes')}.` : 'Not synced yet.'}</p>
                  {progress ? (
                    <div class="sync-progress" role="status">
                      <span class="spinner" aria-hidden="true" />
                      <span>{progress.phase === 'batch' ? `Reading ${progress.name} (${progress.done + 1} of ${progress.total})` : progress.phase === 'finishing' ? 'Saving…' : 'Opening the Batches folder…'}</span>
                    </div>
                  ) : (
                    <Button variant="gold" icon="refresh" onClick={runSync}>Sync now</Button>
                  )}
                  {result ? <Notice tone="ok" icon="done">Done: {plural(result.batches, 'batch', 'batches')}, {plural(result.classes, 'class', 'classes')} ({result.new_classes} new), {plural(result.files, 'file')}.</Notice> : null}
                  {syncError ? <ErrorBox error={syncError} /> : null}
                  <p class="fine"><Icon name="info" />New recordings show up for students after a sync. Sync after each upload, or once a day.</p>
                </>
              ) : (
                <Empty icon="folder" title="Connect your Batches folder" action={<Link class="btn btn-navy" href="/admin/settings"><Icon name="link" /><span>Connect it</span></Link>}>
                  The app reads batches, classes, recordings and notes straight from it.
                </Empty>
              )}
            </div>
          </Section>

          <GroupsCard />

          {data.expiring_soon.length ? (
            <Section title="Access ending in the next 2 weeks" id="a-expiring">
              <ul class="list">
                {data.expiring_soon.map((x) => (
                  <li class="list-row" key={x.enrolment_id}>
                    <span class="list-text"><strong>{x.person.full_name || x.person.email}</strong><small>{x.batch.name} · ends {dateShort(x.valid_until)}</small></span>
                    <Link class="btn btn-ghost btn-sm" href={`/admin/batches/${x.batch.id}`}><span>Open</span></Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {data.consent ? <ConsentOverview c={data.consent} /> : null}

          <Section title="Manage" id="a-manage">
            <div class="tiles">
              <Link class="tile" href="/admin/batches"><Icon name="layers" /><span><strong>Batches</strong><small>Students, groups and teachers</small></span><Icon name="next" /></Link>
              <Link class="tile" href="/admin/people"><Icon name="users" /><span><strong>People</strong><small>Students, parents, teachers and admins</small></span><Icon name="next" /></Link>
              <Link class="tile" href="/admin/payments"><Icon name="receipt" /><span><strong>Payments</strong><small>Website and other payments, receipts</small></span><Icon name="next" /></Link>
              <Link class="tile" href="/attendance"><Icon name="users" /><span><strong>Attendance</strong><small>Registers for recent classes</small></span><Icon name="next" /></Link>
              <Link class="tile" href="/admin/settings"><Icon name="settings" /><span><strong>Settings</strong><small>Library folder, playback, admins, receipts</small></span><Icon name="next" /></Link>
            </div>
          </Section>
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Microsoft groups */
const GROUP_REASON = {
  ended: 'access ended',
  paused: 'account paused',
  not_started: 'access hasn’t started',
  no_batch: 'none of their batches uses this group now',
};
const groupLineId = (x, action) => `${action}:${x.person.id}:${x.group.key}`;

function GroupLine({ x, action, onByHand, busy }) {
  const about = [x.group.name, x.batches.length ? x.batches.join(', ') : '', action === 'remove' ? GROUP_REASON[x.reason] || '' : ''].filter(Boolean);
  return (
    <li class="list-row group-line">
      <span class="list-text">
        <strong>{x.person.name}</strong>
        <small>{about.join(' · ')}</small>
        {x.problem ? <small class="field-error">{x.problem}</small> : null}
      </span>
      {x.problem ? <Button variant="ghost" size="sm" busy={busy} onClick={() => onByHand(x, action)} aria-label={`Done by hand: ${x.person.name}`}>Done by hand</Button> : null}
    </li>
  );
}

function GroupsCard() {
  const toast = useToast();
  const { query } = useRoute();
  const { data, error, reload, setData } = useRpc('admin_group_changes');
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [runErr, setRunErr] = useState(null);
  const [handBusy, setHandBusy] = useState(null);
  const started = useRef(false);

  const run = async () => {
    setRunErr(null);
    setResult(null);
    const token = graphToken('groups');
    if (!token) { await requestGraphAccess('/admin?groups=1', 'groups'); return; }
    try {
      // Work from the list as it is now, not as this page first showed it.
      setProgress({ phase: 'checking' });
      const fresh = await rpc('admin_group_changes');
      setData(fresh);
      const res = await applyGroupChanges(token, fresh, rpc, { onProgress: setProgress });
      setData(res.changes);
      setResult(res.summary);
      const s = res.summary;
      toast(s.problems.length ? `Done, with ${plural(s.problems.length, 'problem')} to look at` : 'Microsoft groups updated', s.problems.length ? 'bad' : 'ok');
    } catch (e) {
      if (e.reason === 'graph_auth' || e.reason === 'graph_forbidden') forgetGraphToken();
      setRunErr(e);
      rpc('admin_group_changes').then(setData).catch(() => {});
    } finally {
      setProgress(null);
    }
  };

  useEffect(() => {
    if (!data || started.current || query.get('groups') !== '1') return;
    started.current = true;
    navigate('/admin', { replace: true });
    if (graphToken('groups')) run();
  }, [data]);

  // The admin made the change in Microsoft 365 themselves. (An add done by hand keeps no group id: the
  // app didn't see which group it was.)
  const byHand = async (x, action) => {
    setHandBusy(groupLineId(x, action));
    try {
      setData(await rpc('admin_group_result', {
        p_results: [{ person_id: x.person.id, group_key: x.group.key, group_name: x.group.name, group_id: action === 'remove' ? x.group.id || null : null, action, ok: true }],
        p_by_hand: true,
      }));
      toast('Marked as done');
    } catch (e) { toast(e.message, 'bad'); }
    setHandBusy(null);
  };

  // Access ended in a batch with no Members group: nothing for the app to change; the admin checks by hand.
  const markRemoved = async (x) => {
    setHandBusy(x.enrolment_id);
    try {
      await rpc('admin_mark_access_removed', { p_enrolment_id: x.enrolment_id });
      setData(await rpc('admin_group_changes'));
      toast('Marked as done');
    } catch (e) { toast(e.message, 'bad'); }
    setHandBusy(null);
  };

  if (error) return <Section title="Microsoft groups" id="a-groups"><ErrorBox error={error} onRetry={reload} /></Section>;
  if (!data) return null;
  const total = data.adds.length + data.removes.length;
  if (!total && !data.no_group.length && !data.ended_no_group.length && !result && !runErr) return null;
  const parts = [data.adds.length ? `${plural(data.adds.length, 'student')} to add` : '', data.removes.length ? `${data.removes.length} to take out` : ''].filter(Boolean);
  const checking = data.adds.some((x) => x.checking) || data.removes.some((x) => x.checking);
  return (
    <Section title="Microsoft groups" id="a-groups">
      <div class="card groups-card">
        <p>{total ? <><strong>{parts.join(', ')}.</strong> Students can play a batch’s recordings because they are in its Members group in Microsoft 365.</> : 'Nothing to change in the batches’ Microsoft groups.'}</p>
        {checking ? <p class="fine"><Icon name="info" /><span>This includes students from before the app kept the groups. It checks each one: adding someone already in a group, or taking out someone who isn’t, changes nothing.</span></p> : null}
        {data.adds.length ? (
          <>
            <h3 class="form-title">To add ({data.adds.length})</h3>
            <ul class="list">{data.adds.map((x) => <GroupLine key={groupLineId(x, 'add')} x={x} action="add" onByHand={byHand} busy={handBusy === groupLineId(x, 'add')} />)}</ul>
          </>
        ) : null}
        {data.removes.length ? (
          <>
            <h3 class="form-title">To take out ({data.removes.length})</h3>
            <ul class="list">{data.removes.map((x) => <GroupLine key={groupLineId(x, 'remove')} x={x} action="remove" onByHand={byHand} busy={handBusy === groupLineId(x, 'remove')} />)}</ul>
          </>
        ) : null}
        {progress ? (
          <div class="sync-progress" role="status">
            <span class="spinner" aria-hidden="true" />
            <span>{progress.phase === 'checking' ? 'Checking what to change…' : progress.phase === 'groups' ? `Finding ${progress.name} in Microsoft 365…` : progress.phase === 'people' ? `Updating ${progress.name} (${progress.done + 1} of ${progress.total})` : 'Saving…'}</span>
          </div>
        ) : total ? (
          <div class="form-actions">
            <Button variant="gold" icon="users" onClick={run}>{graphToken('groups') ? 'Update Microsoft groups' : 'Allow group changes and update'}</Button>
          </div>
        ) : null}
        {result ? (
          <Notice tone={result.problems.length ? 'warn' : 'ok'} icon={result.problems.length ? 'alert' : 'done'}>
            Done: {plural(result.added, 'student')} added, {result.removed} taken out.{result.problems.length ? ` ${plural(result.problems.length, 'problem')} above: fix it in Microsoft 365 or the batch, then update again, or make the change yourself and tap Done by hand.` : ''}
          </Notice>
        ) : null}
        {runErr ? <ErrorBox error={runErr} /> : null}
        {data.ended_no_group.length ? (
          <>
            <h3 class="form-title">Access ended in a batch with no Members group ({data.ended_no_group.length})</h3>
            <p class="fine"><Icon name="info" /><span>The app can’t take these students out of anything. If you gave them the batch’s recordings another way (say, by sharing its folder with them), stop that by hand, then tap Done.</span></p>
            <ul class="list">{data.ended_no_group.map((x) => (
              <li class="list-row" key={x.enrolment_id}>
                <span class="list-text"><strong>{x.person.name}</strong><small>{x.batch.name} · ended {dateShort(x.valid_until)}</small></span>
                <Button variant="ghost" size="sm" busy={handBusy === x.enrolment_id} onClick={() => markRemoved(x)} aria-label={`Done: ${x.person.name}, ${x.batch.name}`}>Done</Button>
              </li>
            ))}</ul>
          </>
        ) : null}
        {data.no_group.length ? (
          <p class="fine"><Icon name="alert" /><span>No Members group set for {data.no_group.map((b) => `${b.batch_name} (${plural(b.students, 'student')})`).join(', ')}. Set it in Admin &gt; Batches &gt; Edit batch.</span></p>
        ) : null}
        <p class="fine"><Icon name="info" /><span>The app uses your own Microsoft account for this, so sign in with the Microsoft 365 Global Administrator account. The first time, Microsoft asks you to allow it. It only changes who is in these groups.</span></p>
      </div>
    </Section>
  );
}

/* ---------------------------------------------------------------- Batches */
function BatchRow({ b }) {
  return (
    <li>
      <Link class="list-row list-link" href={`/admin/batches/${b.id}`}>
        <span class="subject">{b.subject || '•'}</span>
        <span class="list-text">
          <strong>{b.name}</strong>
          <small>{plural(b.class_count, 'class', 'classes')} · {plural(b.active_students, 'active student')}{b.teacher ? ` · ${b.teacher.full_name || b.teacher.email}` : ''}</small>
        </span>
        {b.missing ? <Chip tone="bad">Folder missing</Chip> : b.archived ? <Chip>Archived</Chip> : !b.members_group ? <Chip tone="gold">No group set</Chip> : null}
        <Icon name="next" class="go" />
      </Link>
    </li>
  );
}

export function AdminBatches() {
  const { data, error, loading, reload } = useRpc('admin_batches');
  const groups = useMemo(() => {
    const all = data || [];
    return {
      batches: all.filter((b) => !b.archived && !b.missing && b.kind !== 'revision'),
      revision: all.filter((b) => !b.archived && !b.missing && b.kind === 'revision'),
      other: all.filter((b) => b.archived || b.missing),
    };
  }, [data]);
  return (
    <Shell title="Batches" backTo="/admin" backLabel="Admin">
      {loading && !data ? <Loading rows={5} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {data && !data.length ? (
        <Empty icon="layers" title="No batches yet" action={<Link class="btn btn-navy" href="/admin"><Icon name="refresh" /><span>Sync the library</span></Link>}>
          Batches appear here after the first sync of your Batches folder.
        </Empty>
      ) : null}
      {groups.batches.length ? <Section title="Batches" id="ab-b"><ul class="list">{groups.batches.map((b) => <BatchRow b={b} key={b.id} />)}</ul></Section> : null}
      {groups.revision.length ? <Section title="Revision kits" id="ab-r"><ul class="list">{groups.revision.map((b) => <BatchRow b={b} key={b.id} />)}</ul></Section> : null}
      {groups.other.length ? <Section title="Archived or missing" id="ab-o"><ul class="list">{groups.other.map((b) => <BatchRow b={b} key={b.id} />)}</ul></Section> : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- One batch: details and students */
function EditBatch({ batch, open, onClose, onSaved }) {
  const toast = useToast();
  const staff = useRpc('admin_people', {}, [open]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useLayoutEffect(() => {
    if (open) setForm({ subject: batch.subject || '', kind: batch.kind, members_group: batch.members_group || '', teacher_id: batch.teacher ? batch.teacher.id : '', archived: batch.archived });
  }, [open]);
  if (!form) return null;
  const teachers = (staff.data || []).filter((x) => x.role !== 'student' && x.status === 'active');
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await rpc('admin_update_batch', { p_batch_id: batch.id, p_subject: form.subject, p_kind: form.kind, p_members_group: form.members_group, p_teacher_id: form.teacher_id || null, p_archived: form.archived });
      toast('Batch saved');
      onSaved();
      onClose();
    } catch (err) { toast(err.message, 'bad'); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value });
  return (
    <Sheet title="Edit batch" open={open} onClose={onClose}>
      <form onSubmit={save} class="form">
        <div class="row-2">
          <Field label="Subject">{(id) => (
            <select id={id} value={form.subject} onChange={set('subject')}>
              <option value="">None</option>
              {SUBJECTS.map((s) => <option value={s} key={s}>{s}</option>)}
            </select>
          )}</Field>
          <Field label="Type">{(id) => (
            <select id={id} value={form.kind} onChange={set('kind')}>
              <option value="batch">Batch</option>
              <option value="revision">Revision kit</option>
              <option value="other">Other</option>
            </select>
          )}</Field>
        </div>
        <Field label="Microsoft Members group" hint="The Microsoft 365 group this batch's OneDrive folder is shared with, like “MA December 2025 Members”. The app keeps the batch’s students in it; change it and the app moves them to the new group.">{(id) => (
          <input id={id} type="text" maxLength={200} value={form.members_group} onInput={set('members_group')} placeholder="MA December 2025 Members" />
        )}</Field>
        <Field label="Teacher">{(id) => (
          <select id={id} value={form.teacher_id} onChange={set('teacher_id')}>
            <option value="">Not set</option>
            {teachers.map((t) => <option value={t.id} key={t.id}>{t.full_name || t.email}</option>)}
          </select>
        )}</Field>
        <label class="check"><input type="checkbox" checked={form.archived} onChange={set('archived')} /><span>Archived (hidden from students and teachers)</span></label>
        <div class="form-actions"><Button type="submit" variant="navy" busy={busy}>Save</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
      </form>
    </Sheet>
  );
}

function AccessUntilPicker({ value, onChange, from }) {
  const base = from || todayIso();
  const options = [['12 months', 12], ['2 years', 24], ['3 years', 36]];
  return (
    <Field label="Access until" hint="Silver 12 months · Gold 2 years · Platinum 3 years">{(id) => (
      <>
        <div class="quick">
          {options.map(([label, months]) => {
            const v = addMonths(base, months);
            return <button type="button" key={label} class={`quick-btn${value === v ? ' is-on' : ''}`} onClick={() => onChange(v)}>{label}</button>;
          })}
        </div>
        <input id={id} type="date" value={value} min={base} onInput={(e) => onChange(e.currentTarget.value)} required />
      </>
    )}</Field>
  );
}

// "Several at once": one student per line, as "email", "email, Name" or "Name <email>" (as Outlook copies them).
export function parseStudentLines(text) {
  const seen = new Set();
  const out = [];
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    let email = '';
    let name = '';
    const angle = line.match(/^(.*?)<([^>]+)>\s*[,;]?\s*$/);
    if (angle) {
      email = angle[2].trim();
      name = angle[1].replace(/["',;]/g, ' ').trim();
    } else {
      const [first, ...rest] = line.split(/[,;\t]/);
      email = first.trim();
      name = rest.join(' ').trim();
    }
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ line, email, name: name.replace(/\s+/g, ' ') });
  }
  return out;
}

function AddStudent({ batch, open, onClose, onSaved }) {
  const toast = useToast();
  const blank = () => ({ email: '', full_name: '', list: '', until: addMonths(todayIso(), 12), source: 'Silver plan' });
  const [form, setForm] = useState(blank);
  const [several, setSeveral] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [failed, setFailed] = useState([]);
  useLayoutEffect(() => { if (open) { setErr(null); setFailed([]); setForm(blank()); } }, [open]);
  const enrol = (email, name) => rpc('admin_enrol', { p_batch_id: batch.id, p_email: email, p_valid_until: form.until, p_source: form.source, p_full_name: name });
  const canAddToGroup = Boolean(batch.members_group && graphToken('groups'));
  // With group changes allowed in this tab, the new students go into the batch's group straight away.
  const toGroup = async (emails) => {
    if (!canAddToGroup) return { text: '' };
    try {
      const wanted = new Set(emails.map((e) => e.trim().toLowerCase()));
      const mine = (x, action) => action === 'add' && (x.batch_ids || []).includes(batch.id) && wanted.has(x.person.email);
      const changes = await rpc('admin_group_changes');
      if (!changes.adds.some((x) => mine(x, 'add'))) return { text: '' };
      const { summary } = await applyGroupChanges(graphToken('groups'), changes, rpc, { only: mine });
      if (summary.problems.length) return { text: `. Not added to ${batch.members_group}: ${summary.problems[0].error}`, bad: true };
      return { text: summary.added ? `, and to ${batch.members_group} in Microsoft 365` : '' };
    } catch (e) {
      if (e.reason === 'graph_auth' || e.reason === 'graph_forbidden') forgetGraphToken();
      return { text: `. Not added to ${batch.members_group} yet: ${e.message}`, bad: true };
    }
  };

  const saveOne = async () => {
    await enrol(form.email, form.full_name);
    const extra = await toGroup([form.email]);
    toast(`${form.full_name || form.email} added to ${batch.name}${extra.text}`, extra.bad ? 'bad' : 'ok');
    onSaved();
    onClose();
  };

  const saveMany = async () => {
    const rows = parseStudentLines(form.list);
    if (!rows.length) throw new Error('Paste at least one email address, one per line.');
    const bad = [];
    let added = 0;
    const done = [];
    for (const r of rows) {
      try { await enrol(r.email, r.name); added += 1; done.push(r.email); } catch (ex) { bad.push({ ...r, message: ex.message }); }
    }
    const extra = done.length ? await toGroup(done) : { text: '' };
    if (added) onSaved();
    if (!bad.length) {
      toast(`${plural(added, 'student')} added to ${batch.name}${extra.text}`, extra.bad ? 'bad' : 'ok');
      onClose();
      return;
    }
    setFailed(bad);
    setForm((f) => ({ ...f, list: bad.map((b) => b.line).join('\n') }));
    if (added) toast(`${plural(added, 'student')} added. ${bad.length} still to fix.`);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setFailed([]);
    try { await (several ? saveMany() : saveOne()); } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <Sheet title={`Add students to ${batch.name}`} open={open} onClose={onClose}>
      <form onSubmit={save} class="form">
        <div class="quick" role="group" aria-label="How many students">
          <button type="button" class={`quick-btn${several ? '' : ' is-on'}`} aria-pressed={several ? 'false' : 'true'} onClick={() => { setSeveral(false); setErr(null); }}>One student</button>
          <button type="button" class={`quick-btn${several ? ' is-on' : ''}`} aria-pressed={several ? 'true' : 'false'} onClick={() => { setSeveral(true); setErr(null); }}>Several at once</button>
        </div>
        {several ? (
          <Field label="Academy emails" error={err} hint="One student per line. Add the name after a comma if you like: priya.sharma@vidyavedhacademyllp.onmicrosoft.com, Priya Sharma">{(id) => (
            <textarea id={id} rows={6} value={form.list} required autofocus spellcheck={false} autoComplete="off"
              placeholder={'priya.sharma@vidyavedhacademyllp.onmicrosoft.com, Priya Sharma\nrohan.kulkarni@vidyavedhacademyllp.onmicrosoft.com'}
              onInput={(e) => setForm({ ...form, list: e.currentTarget.value })} />
          )}</Field>
        ) : (
          <>
            <Field label="Academy email" error={err}>{(id) => (
              <input id={id} type="email" autoComplete="off" value={form.email} required autofocus placeholder="name@vidyavedhacademyllp.onmicrosoft.com"
                onInput={(e) => setForm({ ...form, email: e.currentTarget.value })} />
            )}</Field>
            <Field label="Name (optional)">{(id) => (
              <input id={id} type="text" value={form.full_name} onInput={(e) => setForm({ ...form, full_name: e.currentTarget.value })} placeholder="Priya Sharma" />
            )}</Field>
          </>
        )}
        {failed.length ? (
          <div class="notice notice-bad" role="alert">
            <Icon name="alert" />
            <div>
              <p>{failed.length === 1 ? 'This one wasn’t added. Fix it and add again:' : `These ${failed.length} weren’t added. Fix them and add again:`}</p>
              <ul class="fail-list">{failed.map((f) => <li key={f.line}><strong>{f.email || f.line}</strong>: {f.message}</li>)}</ul>
            </div>
          </div>
        ) : null}
        <AccessUntilPicker value={form.until} onChange={(v) => setForm({ ...form, until: v })} />
        <Field label="Paid through">{(id) => (
          <select id={id} value={form.source} onChange={(e) => setForm({ ...form, source: e.currentTarget.value })}>
            {SOURCES.map((s) => <option key={s}>{s}</option>)}
          </select>
        )}</Field>
        {batch.members_group ? (
          <Notice icon="info">{canAddToGroup
            ? <>The app also adds {several ? 'them' : 'the student'} to <strong>{batch.members_group}</strong> in Microsoft 365, so the recordings open for them.</>
            : <>The app adds {several ? 'them' : 'the student'} to <strong>{batch.members_group}</strong> in Microsoft 365 when you tap Update Microsoft groups on the Admin page, so the recordings open for them.</>}</Notice>
        ) : (
          <Notice tone="warn" icon="alert">Set this batch&rsquo;s Microsoft Members group (Edit batch), so the app can add students to it.</Notice>
        )}
        <div class="form-actions">
          <Button type="submit" variant="navy" icon="adduser" busy={busy}>{several ? 'Add students' : 'Add student'}</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Sheet>
  );
}

function EditEnrolment({ row, open, onClose, onSaved }) {
  const toast = useToast();
  const [until, setUntil] = useState(row ? row.valid_until : '');
  const [busy, setBusy] = useState('');
  useLayoutEffect(() => { if (open && row) setUntil(row.valid_until); }, [open, row]);
  if (!row) return null;
  const act = async (kind) => {
    if (kind === 'delete' && !confirm(`Delete ${row.person.full_name || row.person.email}'s enrolment? Use this only for mistakes.`)) return;
    setBusy(kind);
    try {
      if (kind === 'save') await rpc('admin_update_enrolment', { p_enrolment_id: row.enrolment_id, p_valid_until: until });
      if (kind === 'end') await rpc('admin_end_enrolment', { p_enrolment_id: row.enrolment_id });
      if (kind === 'delete') await rpc('admin_delete_enrolment', { p_enrolment_id: row.enrolment_id });
      toast(kind === 'save' ? 'Access updated' : kind === 'end' ? 'Access ended' : 'Enrolment deleted');
      onSaved();
      onClose();
    } catch (e) { toast(e.message, 'bad'); }
    setBusy('');
  };
  return (
    <Sheet title={row.person.full_name || row.person.email} open={open} onClose={onClose}>
      <div class="form">
        <p class="muted">{row.person.email}{row.source ? ` · ${row.source}` : ''} · since {dateShort(row.valid_from)}</p>
        <AccessUntilPicker value={until} onChange={setUntil} from={row.valid_from} />
        <div class="form-actions">
          <Button variant="navy" busy={busy === 'save'} onClick={() => act('save')}>Save date</Button>
          {row.state === 'active' ? <Button variant="line" busy={busy === 'end'} onClick={() => act('end')}>End access now</Button> : null}
          <Button variant="danger" busy={busy === 'delete'} onClick={() => act('delete')}>Delete</Button>
        </div>
      </div>
    </Sheet>
  );
}

export function AdminBatch({ id }) {
  const batches = useRpc('admin_batches');
  const students = useRpc('admin_batch_students', { p_batch_id: id }, [id]);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState(null);
  const batch = batches.data ? batches.data.find((b) => b.id === id) : null;
  const list = students.data || [];
  const active = list.filter((x) => x.state !== 'ended');
  const ended = list.filter((x) => x.state === 'ended');
  const reloadAll = () => { batches.reload(true); students.reload(true); };
  // Where the student stands in the batch's Microsoft group, as far as the app knows.
  const groupText = (x) => {
    const g = x.group;
    if (!g) return '';
    if (g.problem) return ' · Microsoft group: a problem, see Admin';
    const other = x.state !== 'active' ? ' through another batch' : '';
    if (g.state === 'in') return ` · in the Microsoft group${other}`;
    if (g.state === 'to_add') return ` · to add to the Microsoft group${other}`;
    if (g.state === 'to_remove') return ' · to take out of the Microsoft group';
    if (g.state === 'waiting') return ' · Microsoft group checked when access starts';
    if (x.person.status !== 'active') return ' · not in the Microsoft group while paused';
    if (x.state === 'upcoming') return ' · added to the Microsoft group when access starts';
    return x.access_removed_at ? ' · taken out of the Microsoft group' : ' · not in the Microsoft group';
  };
  const stateChip = (x) => x.state === 'active' ? <Chip tone="ok">Active</Chip> : x.state === 'upcoming' ? <Chip tone="gold">Starts {dateShort(x.valid_from)}</Chip> : <Chip>Ended</Chip>;
  const Row = ({ x }) => (
    <li class="list-row">
      <span class="list-text">
        <strong>{x.person.full_name || x.person.email}</strong>
        <small>{x.person.email} · until {dateShort(x.valid_until)} · {plural(x.opened, 'class', 'classes')} opened{x.person.signed_in ? '' : ' · not signed in yet'}{groupText(x)}</small>
      </span>
      {stateChip(x)}
      <button class="icon-btn" type="button" aria-label={`Edit ${x.person.full_name || x.person.email}`} onClick={() => setRow(x)}><Icon name="edit" /></button>
    </li>
  );
  return (
    <Shell backTo="/admin/batches" backLabel="Batches">
      {(batches.loading && !batches.data) ? <Loading rows={4} /> : null}
      {batches.error ? <ErrorBox error={batches.error} onRetry={batches.reload} /> : null}
      {batches.data && !batch ? <Empty icon="search" title="Batch not found" /> : null}
      {batch ? (
        <>
          <div class="batch-head">
            <span class="subject subject-lg">{batch.subject || '•'}</span>
            <div>
              <h1 class="page-title">{batch.name}</h1>
              <p class="muted">{batch.kind === 'revision' ? 'Revision kit' : batch.start_month ? `Started ${monthYear(batch.start_month)}` : 'Batch'} · {plural(batch.class_count, 'class', 'classes')}</p>
            </div>
          </div>
          <dl class="facts card">
            <div><dt>Members group</dt><dd>{batch.members_group || <span class="muted">Not set</span>}</dd></div>
            <div><dt>Teacher</dt><dd>{batch.teacher ? batch.teacher.full_name || batch.teacher.email : <span class="muted">Not set</span>}</dd></div>
            <div><dt>Status</dt><dd>{batch.missing ? 'Folder missing in OneDrive' : batch.archived ? 'Archived' : 'Live'}</dd></div>
          </dl>
          <div class="row">
            <Button variant="line" icon="edit" onClick={() => setEditing(true)}>Edit batch</Button>
            <Link class="btn btn-ghost" href={`/batches/${batch.id}`}><Icon name="book" /><span>See classes</span></Link>
            {msUrl(batch.web_url) ? <Button variant="ghost" icon="external" href={msUrl(batch.web_url)} target="_blank" rel="noopener">OneDrive folder</Button> : null}
          </div>

          <Section title={`Students (${active.length})`} id="abs-s" action={<Button variant="gold" size="sm" icon="adduser" onClick={() => setAdding(true)}>Add students</Button>}>
            {students.loading && !students.data ? <Loading rows={3} /> : null}
            {students.error ? <ErrorBox error={students.error} onRetry={students.reload} /> : null}
            {students.data && !active.length ? <p class="muted">No students with access yet.</p> : null}
            {active.length ? <ul class="list">{active.map((x) => <Row x={x} key={x.enrolment_id} />)}</ul> : null}
          </Section>
          {ended.length ? (
            <Section title={`Access ended (${ended.length})`} id="abs-e">
              <ul class="list">{ended.map((x) => <Row x={x} key={x.enrolment_id} />)}</ul>
            </Section>
          ) : null}
          <EditBatch batch={batch} open={editing} onClose={() => setEditing(false)} onSaved={reloadAll} />
          <AddStudent batch={batch} open={adding} onClose={() => setAdding(false)} onSaved={reloadAll} />
          <EditEnrolment row={row} open={!!row} onClose={() => setRow(null)} onSaved={reloadAll} />
        </>
      ) : null}
    </Shell>
  );
}

/* ---------------------------------------------------------------- People */
function EditPerson({ person, open, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useLayoutEffect(() => {
    if (open) {
      setErr(null);
      setForm(person
        ? { email: person.email, full_name: person.full_name || '', role: person.role, status: person.status, phone: person.phone || '', contact_email: person.contact_email || '' }
        : { email: '', full_name: '', role: 'student', status: 'active', phone: '', contact_email: '' });
    }
  }, [open, person]);
  if (!form) return null;
  const parent = Boolean(person && person.role === 'parent');
  const set = (k) => (e) => setForm({ ...form, [k]: e.currentTarget.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await rpc('admin_save_person', { p_email: form.email, p_full_name: form.full_name, p_role: form.role, p_status: form.status });
      if (!parent && (form.phone !== ((person && person.phone) || '') || form.contact_email !== ((person && person.contact_email) || ''))) {
        await rpc('admin_save_contact', { p_email: form.email, p_phone: form.phone, p_contact_email: form.contact_email || null });
      }
      toast(person ? 'Saved' : `${form.full_name || form.email} added`);
      onSaved(); onClose();
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  return (
    <Sheet title={person ? 'Edit person' : 'Add a person'} open={open} onClose={onClose}>
      <form class="form" onSubmit={save}>
        <Field label={parent ? 'Email (their Google account)' : 'Academy email'} error={err}>{(id) => <input id={id} type="email" value={form.email} required disabled={!!person} onInput={set('email')} autofocus={!person} />}</Field>
        <Field label="Name">{(id) => <input id={id} type="text" value={form.full_name} onInput={set('full_name')} />}</Field>
        <div class="row-2">
          <Field label="Role">{(id) => (parent ? (
            <select id={id} value="parent" disabled><option value="parent">Parent</option></select>
          ) : (
            <select id={id} value={form.role} onChange={set('role')}>
              <option value="student">Student</option><option value="teacher">Teacher</option><option value="admin">Admin</option>
            </select>
          ))}</Field>
          <Field label="Status">{(id) => (
            <select id={id} value={form.status} onChange={set('status')}>
              <option value="active">Active</option><option value="blocked">Paused</option>
            </select>
          )}</Field>
        </div>
        {parent ? <p class="fine"><Icon name="info" />Parents are added and removed on their student’s page. Pausing a parent stops them signing in.</p> : (
          <>
            <div class="row-2">
              <Field label="Mobile (optional)">{(id) => <input id={id} type="tel" inputMode="tel" value={form.phone} onInput={set('phone')} placeholder="98123 45678" />}</Field>
              <Field label="Personal email (optional)">{(id) => <input id={id} type="email" value={form.contact_email} onInput={set('contact_email')} placeholder="name@gmail.com" />}</Field>
            </div>
            <p class="fine"><Icon name="info" />With a mobile or personal email saved, the student’s payments on the website link to them by themselves.</p>
          </>
        )}
        <div class="form-actions"><Button type="submit" variant="navy" busy={busy}>Save</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>
      </form>
    </Sheet>
  );
}

export function AdminPeople() {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [role, setRole] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined closed, null new, object edit
  useEffect(() => { const t = setTimeout(() => setTerm(search), 250); return () => clearTimeout(t); }, [search]);
  const people = useRpc('admin_people', { p_search: term || null, p_role: role || null }, [term, role]);
  const roleLabel = ROLE_LABEL;
  return (
    <Shell title="People" backTo="/admin" backLabel="Admin">
      <div class="toolbar">
        <label class="search"><Icon name="search" /><span class="sr-only">Search people</span>
          <input type="search" placeholder="Search name or email" value={search} onInput={(e) => setSearch(e.currentTarget.value)} />
        </label>
        <Button variant="gold" size="sm" icon="plus" onClick={() => setEditing(null)}>Add</Button>
      </div>
      <div class="filters" role="group" aria-label="Show">
        {[['', 'Everyone'], ['student', 'Students'], ['parent', 'Parents'], ['teacher', 'Teachers'], ['admin', 'Admins']].map(([v, l]) => (
          <button type="button" key={v} class={`quick-btn${role === v ? ' is-on' : ''}`} aria-pressed={role === v ? 'true' : 'false'} onClick={() => setRole(v)}>{l}</button>
        ))}
      </div>
      {people.loading && !people.data ? <Loading rows={5} /> : null}
      {people.error ? <ErrorBox error={people.error} onRetry={people.reload} /> : null}
      {people.data && !people.data.length ? <Empty icon="users" title="Nobody found">Try another name, or add the person.</Empty> : null}
      {people.data && people.data.length ? (
        <ul class="list">
          {people.data.map((x) => (
            <li class="list-row" key={x.id}>
              <span class="list-text">
                <strong><Link href={`/admin/people/${x.id}`} class="person-link">{x.full_name || x.email}</Link></strong>
                <small>
                  {x.email} · {x.signed_in ? `last seen ${x.last_seen_at ? dateShort(x.last_seen_at) : '—'}` : 'not signed in yet'}
                  {x.role === 'student' ? ` · ${plural(x.active_batches, 'active batch', 'active batches')}` : ''}
                  {x.role === 'student' && x.minor ? ' · under 18' : ''}
                  {x.role === 'parent' && x.children && x.children.length ? ` · parent of ${x.children.join(', ')}` : ''}
                </small>
              </span>
              {x.status === 'blocked' ? <Chip tone="bad">Paused</Chip> : <Chip tone={x.role === 'admin' ? 'navy' : x.role === 'teacher' ? 'gold' : 'plain'}>{roleLabel[x.role]}</Chip>}
              <button class="icon-btn" type="button" aria-label={`Edit ${x.full_name || x.email}`} onClick={() => setEditing(x)}><Icon name="edit" /></button>
            </li>
          ))}
        </ul>
      ) : null}
      <EditPerson person={editing || null} open={editing !== undefined} onClose={() => setEditing(undefined)} onSaved={() => people.reload(true)} />
    </Shell>
  );
}

/* ---------------------------------------------------------------- One person: dates of birth, parents, consent */
const ROLE_LABEL = { student: 'Student', teacher: 'Teacher', admin: 'Admin', parent: 'Parent' };
const RELATIONS = [['mother', 'Mother'], ['father', 'Father'], ['guardian', 'Guardian'], ['parent', 'Parent']];

function StateChip({ state }) {
  if (state === 'active') return <Chip tone="ok">Can see progress</Chip>;
  if (state === 'needs_parent') return <Chip tone="bad">No consent yet</Chip>;
  if (state === 'needs_student') return <Chip>Student hasn’t shared</Chip>;
  return null;
}

function consentText(c) {
  if (!c) return '';
  if (c.method === 'recorded') return `Consent recorded by ${c.given_by || 'an admin'} on ${dateShort(c.given_at)}${c.note ? `: ${c.note}` : ''}`;
  if (c.kind === 'parent') return `Consent given in the app on ${dateShort(c.given_at)}`;
  return `The student chose to share on ${dateShort(c.given_at)}`;
}

function BirthDate({ person, onSaved }) {
  const toast = useToast();
  const [value, setValue] = useState(person.birth_date || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { setValue(person.birth_date || ''); }, [person.birth_date]);
  const hint = !person.birth_date
    ? 'Not entered yet, so the app treats them as 18 or over: they decide what each parent sees.'
    : person.minor ? 'Under 18: a parent or guardian gives consent before seeing their progress.' : '18 or over: they decide what each parent sees.';
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      onSaved(await rpc('admin_set_birth_date', { p_student_id: person.id, p_birth_date: value || null }));
      toast('Date of birth saved');
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  return (
    <form class="card form" onSubmit={save}>
      <Field label="Date of birth" error={err} hint={hint}>{(id) => <input id={id} type="date" value={value} max={todayIso()} onInput={(e) => setValue(e.currentTarget.value)} />}</Field>
      <div class="form-actions"><Button type="submit" variant="navy" busy={busy} disabled={(value || '') === (person.birth_date || '')}>Save date of birth</Button></div>
    </form>
  );
}

function ParentRow({ student, x, onChange }) {
  const toast = useToast();
  const [mode, setMode] = useState(''); // '', 'record', 'withdraw', 'remove'
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const label = x.name || x.email;
  const run = async (fn, args, done) => {
    setBusy(true); setErr(null);
    try { onChange(await rpc(fn, args)); toast(done); setMode(''); setNote(''); } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  const cancel = () => { setMode(''); setNote(''); setErr(null); };
  return (
    <li class="parent-row">
      <div class="parent-head">
        <span class="list-text">
          <strong>{label}</strong>
          <small>{RELATION_LABEL[x.relation] || 'Parent'} · {x.email} · {x.signed_in ? 'has signed in' : 'hasn’t signed in yet'}</small>
        </span>
        <StateChip state={x.state} />
      </div>
      {x.consent ? <p class="parent-consent"><Icon name="done" /><span>{consentText(x.consent)}</span></p>
        : x.withdrawn_at ? <p class="parent-consent is-off"><Icon name="userx" /><span>Consent withdrawn {dateShort(x.withdrawn_at)}</span></p> : null}
      {mode === 'record' || mode === 'withdraw' ? (
        <form class="form parent-form" onSubmit={(e) => {
          e.preventDefault();
          run('admin_record_consent', { p_student_id: student.id, p_parent_id: x.id, p_given: mode === 'record', p_note: note },
            mode === 'record' ? 'Consent recorded' : 'Withdrawal recorded');
        }}>
          <Field label={mode === 'record' ? 'Where the consent was given' : 'How the academy was told (optional)'} error={err}
            hint={mode === 'record' ? 'Such as: Admission form signed on 1 Sep 2026. The parent sees this note in the app. Keep the signed form safe.' : 'Such as: Phoned on 5 Oct.'}>
            {(id) => <input id={id} type="text" value={note} maxLength={300} autofocus onInput={(e) => setNote(e.currentTarget.value)} />}
          </Field>
          <div class="form-actions">
            <Button type="submit" variant={mode === 'record' ? 'navy' : 'danger'} size="sm" busy={busy}>{mode === 'record' ? 'Record consent' : 'Record the withdrawal'}</Button>
            <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          </div>
        </form>
      ) : mode === 'remove' ? (
        <Notice tone="warn" icon="alert"><div>
          <span>Take {label} off {student.full_name || student.email}? Any consent between them ends.</span>
          {err ? <span class="field-error" role="alert">{err}</span> : null}
          <span class="row">
            <Button variant="danger" size="sm" busy={busy} onClick={() => run('admin_unlink_parent', { p_student_id: student.id, p_parent_id: x.id }, `${label} removed`)}>Remove</Button>
            <Button variant="ghost" size="sm" onClick={cancel}>Cancel</Button>
          </span>
        </div></Notice>
      ) : (
        <div class="row row-tight">
          {student.minor && x.state === 'needs_parent' ? <Button variant="line" size="sm" icon="notice" onClick={() => setMode('record')}>Record consent from a form</Button> : null}
          {student.minor && x.state === 'active' ? <Button variant="ghost" size="sm" icon="userx" onClick={() => setMode('withdraw')}>Record a withdrawal</Button> : null}
          <Button variant="ghost" size="sm" icon="trash" onClick={() => setMode('remove')} aria-label={`Remove ${label}`}>Remove</Button>
        </div>
      )}
    </li>
  );
}

function AddParent({ student, onAdded }) {
  const toast = useToast();
  const blank = { name: '', email: '', relation: 'mother' };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => { setF({ ...f, [k]: e.currentTarget.value }); setErr(null); };
  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      onAdded(await rpc('admin_link_parent', { p_student_id: student.id, p_email: f.email, p_name: f.name, p_relation: f.relation }));
      toast(`${f.name || f.email} added`);
      setF(blank);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };
  return (
    <form class="card form" onSubmit={add} aria-labelledby="add-parent-title">
      <h3 id="add-parent-title" class="form-title">Add a parent or guardian</h3>
      <div class="row-2">
        <Field label="Parent’s name">{(id) => <input id={id} type="text" value={f.name} onInput={set('name')} autoComplete="off" />}</Field>
        <Field label="Relation">{(id) => <select id={id} value={f.relation} onChange={set('relation')}>{RELATIONS.map(([v, l]) => <option value={v} key={v}>{l}</option>)}</select>}</Field>
      </div>
      <Field label="Parent’s email" error={err} hint="The email from the admission form. They sign in with the Google account for it: a Gmail address, or another email that has a Google account.">
        {(id) => <input id={id} type="email" value={f.email} required onInput={set('email')} placeholder="name@gmail.com" autoComplete="off" />}
      </Field>
      <div class="form-actions"><Button type="submit" variant="navy" icon="adduser" busy={busy}>Add parent</Button></div>
    </form>
  );
}

export function AdminPerson({ id }) {
  const { data, error, loading, reload, setData } = useRpc('admin_person', { p_person_id: id }, [id]);
  const [editing, setEditing] = useState(false);
  const x = data && data.person;
  return (
    <Shell backTo="/admin/people" backLabel="People">
      {loading && !data ? <Loading rows={4} /> : null}
      {error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {x ? (
        <>
          <div class="person-head">
            <span class="avatar" aria-hidden="true">{initialOf(x.full_name || x.email)}</span>
            <div>
              <h1 class="page-title">{x.full_name || x.email}</h1>
              <p class="muted">{x.email.replace('@', '@\u200b')}</p>
              <p class="chips">
                {x.status === 'blocked' ? <Chip tone="bad">Paused</Chip> : <Chip tone={x.role === 'admin' ? 'navy' : x.role === 'teacher' ? 'gold' : 'plain'}>{ROLE_LABEL[x.role]}</Chip>}
                {x.role === 'student' && x.minor ? <Chip tone="gold">Under 18</Chip> : null}
                <span class="muted">{x.signed_in ? `Last seen ${x.last_seen_at ? dateTime(x.last_seen_at) : '—'}` : 'Hasn’t signed in yet'}</span>
              </p>
            </div>
          </div>
          <div class="row"><Button variant="line" size="sm" icon="edit" onClick={() => setEditing(true)}>Edit details</Button></div>
          {x.role === 'student' ? (
            <>
              <Section title="Batches" id="ap-batches">
                {data.batches.length ? (
                  <ul class="list">
                    {data.batches.map((b) => (
                      <li key={b.id}><Link class="list-row list-link" href={`/admin/batches/${b.id}`}>
                        <span class="list-text"><strong>{b.name}</strong><small>{dateShort(b.valid_from)} to {dateShort(b.valid_until)}</small></span>
                        <Chip tone={b.active ? 'ok' : 'plain'}>{b.active ? 'Access on' : 'Not now'}</Chip>
                        <Icon name="next" class="go" />
                      </Link></li>
                    ))}
                  </ul>
                ) : <p class="muted">Not in a batch.</p>}
              </Section>
              <Section title="Age" id="ap-dob"><BirthDate person={x} onSaved={setData} /></Section>
              <Section title="Parents and guardians" id="ap-parents">
                <p class="muted">
                  {x.minor
                    ? 'Each parent gives consent in the app (or you record consent from a signed form) before they see this student’s progress.'
                    : 'This student decides, in the app, whether each parent sees their progress.'}
                </p>
                {data.parents.length ? (
                  <ul class="parent-list">{data.parents.map((pr) => <ParentRow key={pr.id} student={x} x={pr} onChange={setData} />)}</ul>
                ) : null}
                {data.parents.length < 4 ? <AddParent student={x} onAdded={setData} /> : <p class="fine"><Icon name="info" />A student can have up to four parents or guardians in the app.</p>}
              </Section>
            </>
          ) : null}
          {x.role === 'parent' ? (
            <Section title="Students" id="ap-children">
              {data.children.length ? (
                <ul class="list">
                  {data.children.map((c) => (
                    <li key={c.id}><Link class="list-row list-link" href={`/admin/people/${c.id}`}>
                      <span class="list-text"><strong>{c.name}</strong><small>{RELATION_LABEL[c.relation] || 'Parent'} · {c.email}</small></span>
                      <StateChip state={c.state} />
                      <Icon name="next" class="go" />
                    </Link></li>
                  ))}
                </ul>
              ) : <Empty icon="family" title="Not linked to a student">Parents are added on a student’s page.</Empty>}
            </Section>
          ) : null}
          <EditPerson person={x} open={editing} onClose={() => setEditing(false)} onSaved={() => reload(true)} />
        </>
      ) : null}
    </Shell>
  );
}

// On the admin home: students under 18 without a parent's consent, and recent withdrawals.
function ConsentOverview({ c }) {
  if (!c.minors_waiting.length && !c.withdrawn.length && !c.no_birth_date) return null;
  return (
    <Section title="Parents and consent" id="a-consent">
      {c.withdrawn.length ? (
        <>
          <p class="muted">Consent withdrawn in the last 30 days. Talk to the family about the student’s classes.</p>
          <ul class="list">
            {c.withdrawn.map((w) => (
              <li key={w.student_id + w.withdrawn_at}><Link class="list-row list-link" href={`/admin/people/${w.student_id}`}>
                <span class="list-text"><strong>{w.student}</strong><small>{w.parent} withdrew consent · {dateShort(w.withdrawn_at)}</small></span>
                <Chip tone="bad">Withdrawn</Chip><Icon name="next" class="go" />
              </Link></li>
            ))}
          </ul>
        </>
      ) : null}
      {c.minors_waiting.length ? (
        <>
          <p class="muted">Under 18, with no parent’s consent yet ({c.minors_waiting.length}):</p>
          <ul class="list">
            {c.minors_waiting.map((m) => (
              <li key={m.id}><Link class="list-row list-link" href={`/admin/people/${m.id}`}>
                <span class="list-text"><strong>{m.name}</strong><small>{m.parents ? `${plural(Number(m.parents), 'parent')} added, no consent yet` : 'No parent added yet'}</small></span>
                <Icon name="next" class="go" />
              </Link></li>
            ))}
          </ul>
        </>
      ) : null}
      {c.no_birth_date ? (
        <p class="fine"><Icon name="cake" /><span>{c.no_birth_date === 1 ? '1 student has' : `${c.no_birth_date} students have`} no date of birth in the app, so they count as 18 or over. Add it on each student’s page in People.</span></p>
      ) : null}
      {Number(c.parents) ? <p class="fine"><Icon name="family" /><span>{plural(Number(c.parents), 'parent')} in the app, {c.parents_signed_in} signed in.</span></p> : null}
    </Section>
  );
}

/* ---------------------------------------------------------------- Settings */
export function AdminSettings() {
  const { query } = useRoute();
  const toast = useToast();
  const settings = useRpc('admin_settings');
  const [link, setLink] = useState(() => sessionStorage.getItem('vv-pending-root') || '');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState(null);
  const [admins, setAdmins] = useState('');
  const [mode, setMode] = useState('auto');
  const [review, setReview] = useState('');
  const [reviewErr, setReviewErr] = useState(null);
  const resumed = useRef(false);
  useLayoutEffect(() => {
    if (!settings.data) return;
    setAdmins((settings.data.admin_emails || []).join('\n'));
    setMode(settings.data.playback_mode || 'auto');
    setReview((settings.data.review && settings.data.review.email) || '');
  }, [settings.data]);

  const connect = async () => {
    setErr(null);
    const token = graphToken();
    if (!token) {
      sessionStorage.setItem('vv-pending-root', link);
      await requestGraphAccess('/admin/settings?connect=1');
      return;
    }
    setBusy('connect');
    try {
      const root = await resolveFolder(token, link);
      await rpc('admin_save_setting', { p_key: 'library_root', p_value: root });
      sessionStorage.removeItem('vv-pending-root');
      setLink('');
      toast(`Connected to the ${root.name} folder`);
      settings.reload(true);
    } catch (e) {
      if (e.reason === 'graph_auth') forgetGraphToken();
      setErr(e.message);
    }
    setBusy('');
  };

  useEffect(() => {
    if (resumed.current || query.get('connect') !== '1' || !settings.data) return;
    resumed.current = true;
    navigate('/admin/settings', { replace: true });
    if (link && graphToken()) connect();
  }, [settings.data]);

  const saveMode = async (v) => {
    setMode(v);
    try { await rpc('admin_save_setting', { p_key: 'playback_mode', p_value: v }); toast('Playback setting saved'); }
    catch (e) { toast(e.message, 'bad'); }
  };
  const saveAdmins = async (e) => {
    e.preventDefault();
    setBusy('admins');
    try {
      const list = admins.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
      await rpc('admin_save_setting', { p_key: 'admin_emails', p_value: list });
      toast('Admin list saved');
      settings.reload(true);
    } catch (ex) { toast(ex.message, 'bad'); }
    setBusy('');
  };
  const saveReview = async (e) => {
    e.preventDefault();
    setBusy('review');
    setReviewErr(null);
    try {
      settings.setData(await rpc('admin_set_review_account', { p_email: review.trim() }));
      toast(review.trim() ? 'Review account saved' : 'Review account switched off');
    } catch (ex) { setReviewErr(ex.message); }
    setBusy('');
  };
  const root = settings.data && settings.data.library_root;
  const rv = settings.data && settings.data.review;
  return (
    <Shell title="Settings" backTo="/admin" backLabel="Admin">
      {settings.loading && !settings.data ? <Loading rows={4} /> : null}
      {settings.error ? <ErrorBox error={settings.error} onRetry={settings.reload} /> : null}
      {settings.data ? (
        <>
          <Section title="Lecture library" id="s-lib">
            <div class="card form">
              {root ? <p class="library-root"><Icon name="folder" /><span>Connected to <strong>{root.name}</strong>{msUrl(root.web_url) ? <> · <a href={msUrl(root.web_url)} target="_blank" rel="noopener">Open in OneDrive</a></> : null}</span></p> : <p>Connect the OneDrive folder that holds your batch folders.</p>}
              <Field label={root ? 'Change folder' : 'Link to the Batches folder'} error={err} hint="In OneDrive, select the Batches folder and choose Copy link, or copy the address from the browser while the folder is open.">{(id) => (
                <input id={id} type="url" inputMode="url" value={link} placeholder="https://vidyavedhacademyllp-my.sharepoint.com/…" onInput={(e) => setLink(e.currentTarget.value)} />
              )}</Field>
              <div class="form-actions">
                <Button variant="navy" icon="link" busy={busy === 'connect'} disabled={!link.trim()} onClick={connect}>{graphToken() ? 'Connect folder' : 'Allow OneDrive access and connect'}</Button>
              </div>
              <p class="fine"><Icon name="info" />Microsoft will ask you to allow the app to read files you can open. It only reads; it never changes or shares anything.</p>
            </div>
          </Section>

          <Section title="How recordings play" id="s-play">
            <div class="card radios" role="radiogroup" aria-label="How recordings play">
              {[
                ['auto', 'Automatic', 'Inside the app where the browser allows it; in a new tab on iPhone, iPad and Safari.'],
                ['embed', 'Always inside the app', 'Students stay in the app. Some browsers may show a sign-in box instead of the video.'],
                ['newtab', 'Always in a new tab', 'Opens Microsoft’s player. Works everywhere.'],
              ].map(([v, title, text]) => (
                <label class={`radio${mode === v ? ' is-on' : ''}`} key={v}>
                  <input type="radio" name="mode" value={v} checked={mode === v} onChange={() => saveMode(v)} />
                  <span><strong>{title}</strong><small>{text}</small></span>
                </label>
              ))}
            </div>
          </Section>

          <Section title="Receipt details" id="s-receipts">
            <ReceiptDetails />
          </Section>

          <Section title="Google Play review account" id="s-review">
            <form class="card form" onSubmit={saveReview}>
              <p>Google’s reviewers can’t use an academy Microsoft account, so they sign in with this email and a password instead (set the password in Supabase). It works for this one account only, as a student who sees just the batches you add it to: put it in a sample batch.</p>
              <Field label="Review account email" error={reviewErr} hint="An address nobody signs in with, like play.review@vidyavedhacademyllp.onmicrosoft.com. Leave it empty to switch the review sign-in off (the account is paused).">{(id) => (
                <input id={id} type="email" autoComplete="off" value={review} placeholder="play.review@vidyavedhacademyllp.onmicrosoft.com" onInput={(e) => setReview(e.currentTarget.value)} />
              )}</Field>
              {rv ? (
                <p class="fine"><Icon name="info" /><span>{rv.signed_in ? `Signed in${rv.first_sign_in_at ? ` first on ${dateShort(rv.first_sign_in_at)}` : ''}` : 'Not signed in yet'} · {rv.batches.length ? `in ${rv.batches.join(', ')}` : 'not in any batch yet: add it to a sample batch'}{rv.status !== 'active' ? ' · paused' : ''}</span></p>
              ) : null}
              <div class="form-actions"><Button type="submit" variant="navy" busy={busy === 'review'}>Save review account</Button></div>
            </form>
          </Section>

          <Section title="Admins" id="s-admins">
            <form class="card form" onSubmit={saveAdmins}>
              <Field label="Admin emails" hint="One per line. These accounts get the Admin area when they sign in.">{(id) => (
                <textarea id={id} rows={4} value={admins} onInput={(e) => setAdmins(e.currentTarget.value)} />
              )}</Field>
              <div class="form-actions"><Button type="submit" variant="navy" busy={busy === 'admins'}>Save admins</Button></div>
            </form>
          </Section>
        </>
      ) : null}
    </Shell>
  );
}
